<template>
  <div :class="['tw-flex tw-gap-3 tw-rounded tw-p-3', levelRowBgClass(msg.level)]">
    <div :class="['tw-w-0.5 tw-shrink-0 tw-self-stretch tw-rounded-full', levelBgClass(msg.level)]" />
    <div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
      <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
        <div class="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
          <span class="tw-text-sm tw-font-bold" :class="levelTextClass(msg.level)">{{ msg.title }}</span>
          <span v-if="msg.station_label" class="tw-text-xs tw-opacity-50">{{ msg.station_label }}</span>
        </div>
        <div class="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
          <span class="tw-text-xs tw-tabular-nums tw-opacity-60">{{ formatLocalTime(msg.created_at) }}</span>
          <CircularButton
            v-if="sectionKey !== 'inactive' && bellIcon"
            :icon="bellIcon"
            color="transparentWhite"
            shape="rounded"
            size="small"
            :class="levelTextClass(msg.level)"
            :aria-label="bellLabel"
            @click="$emit('toggle-mute')"
          />
        </div>
      </div>

      <p ref="textEl" :class="['tw-text-sm', !expandedText && 'tw-line-clamp-3']">{{ msg.text }}</p>

      <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <Button
          v-if="isOverflowing || expandedText"
          color="transparent"
          size="small"
          @click="expandedText = !expandedText"
        >
          {{ expandedText ? '▲ less' : '▶ more' }}
        </Button>
        <Button
          v-if="msg.details"
          color="semiTransparentWhite"
          size="small"
          class="tw-ml-auto"
          @click="$emit('toggle-detail')"
        >
          <span class="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap">
            <Icon
              name="chevron-down"
              :class="['tw-size-4 tw-fill-current tw-transition-transform', expandedDetail ? 'tw-rotate-0' : '-tw-rotate-90']"
            />
            {{ expandedDetail ? $t('notifications_modal.actions.hide_details') : $t('notifications_modal.actions.show_details') }}
          </span>
        </Button>
      </div>

      <pre
        v-if="msg.details && expandedDetail"
        class="tw-whitespace-pre-wrap tw-rounded tw-bg-white/10 tw-p-3 tw-text-sm"
      >{{ msg.details }}</pre>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, CircularButton, Icon } from '@slideslive/fuse-kit/vue';

import { formatLocalTime } from '@/composables/state_notifications';

const props = defineProps({
  msg: { type: Object, required: true },
  sectionKey: { type: String, required: true },
  muted: { type: Boolean, default: false },
  bellIcon: { type: [Object, String], default: null },
  expandedDetail: { type: Boolean, default: false },
});

defineEmits(['toggle-mute', 'toggle-detail']);

const { t } = useI18n();

const bellLabel = computed(() =>
  t(props.muted ? 'notifications_modal.actions.unmute' : 'notifications_modal.actions.mute'),
);

const levelTextClass = (level) =>
  ({ error: 'tw-text-state-err', warning: 'tw-text-state-warn', info: 'tw-text-state-info' })[level] ?? '';

const levelBgClass = (level) =>
  ({ error: 'tw-bg-state-err', warning: 'tw-bg-state-warn', info: 'tw-bg-state-info' })[level] ?? '';

const levelRowBgClass = (level) =>
  ({ error: 'tw-bg-state-err/10', warning: 'tw-bg-state-warn/10', info: 'tw-bg-state-info/10' })[level] ?? '';

const expandedText = ref(false);
const textEl = ref(null);
const isOverflowing = ref(false);

onMounted(() => {
  if (textEl.value) {
    isOverflowing.value = textEl.value.scrollHeight > textEl.value.clientHeight;
  }
});
</script>
